import React from "react";

/**
 * MatchTopBar
 * ----------------------------------------------------------------------------
 * Reference-style title bar that sits directly under the green header on the
 * match-details page (e.g. "Cricket" centered, with a TV icon on the right).
 *
 * Clicking the TV icon toggles the Live/Scoreboard + stream section below it
 * (handled by the parent via `collapsed` / `onToggleTv`). Nothing is removed —
 * this only wraps the existing TV/Scoreboard UI in a collapsible panel.
 *
 * Props:
 *   title        - sport/label text to show centered (e.g. "Cricket")
 *   collapsed    - whether the TV/Scoreboard panel is hidden
 *   onToggleTv   - callback to flip the collapsed state
 */
const MatchTopBar = ({ title = "Cricket", collapsed = false, onToggleTv }) => {
  return (
    <>
      <style>{mtbStyles}</style>
      <div className="mtb-bar">
        <span className="mtb-title">{title}</span>
        <button
          type="button"
          className={`mtb-tv ${collapsed ? "" : "mtb-tv--on"}`}
          aria-label="Toggle live / scoreboard"
          onClick={onToggleTv}
        >
          {/* TV / monitor icon */}
          <svg viewBox="0 0 24 24" width="22" height="22" aria-hidden="true">
            <path
              fill="currentColor"
              d="M3 4h18a1 1 0 0 1 1 1v11a1 1 0 0 1-1 1h-7v2h3v2H7v-2h3v-2H3a1 1 0 0 1-1-1V5a1 1 0 0 1 1-1zm1 2v9h16V6H4z"
            />
          </svg>
        </button>
      </div>
    </>
  );
};

const mtbStyles = `
.mtb-bar{
  position:relative;
  display:flex;
  align-items:center;
  justify-content:center;
  background:#263e4e;
  color:#fff;
  /* push below the fixed green top header so the bar isn't hidden behind it */
  margin-top:14.5vw;
  height:10.6vw;
  font-size:4.26667vw;
  font-weight:700;
  letter-spacing:.3px;
}
.mtb-title{ line-height:1; font-size:16px; }
.mtb-tv{
  position:absolute;
  right:3.2vw;
  top:50%;
  transform:translateY(-50%);
  display:flex;
  align-items:center;
  justify-content:center;
  background:transparent;
  border:none;
  padding:1vw;
  cursor:pointer;
  /* attention-grabbing smooth fade between red and white */
  animation:mtbTvBlink 1s ease-in-out infinite;
}
.mtb-tv--on{ animation:mtbTvBlink 1s ease-in-out infinite; }
@keyframes mtbTvBlink{
  0%,100% { color:#e23b3b; }   /* red */
  50%     { color:#ffffff; }   /* white */
}
/* The theme gives .tv-tab a big top padding (4.5rem) meant to clear the
   fixed header. The "Cricket" bar now occupies that space, so the padding
   just leaves an empty dark box above the Live/Scoreboard tabs — remove it. */
.tv-tab{
  padding-top:0 !important;
  background-image:none !important;
  background:#303030 !important;
}
/* Mirror the reference exchange's tab CSS:
   .items  { background; width:100%; color; white-space:nowrap; text-align:center;
             font-size:13px; padding:5px 0; border-right:1px solid <border>; }
   .ActiveStreamTab { border-bottom:1px solid <active-border>; border-top:none; } */
.tv-tab li button{
  width:100%;
  background:#303030 !important;
  color:#cfd8dd !important;
  white-space:nowrap !important;
  text-align:center !important;
  font-size:13px !important;
  font-weight:600 !important;
  padding:5px 0 !important;
  border:none !important;
}
/* vertical divider on the right edge of the first tab */
.tv-tab li:first-child button{
  border-right:1px solid #555 !important;
}
.tv-tab li button.active{
  background:#303030 !important;
  color:#fff !important;
  border-bottom:1px solid #e0e6e6 !important;
  border-top:none !important;
}
`;

export default MatchTopBar;
